import { LEGACY_PRO_ATTRIBUTES, PlanAttributes } from '../elements/PlanAttributes'
import { useModals } from '../../store/modals'
import { useCallback, useEffect, useState } from 'react'
import { CreditCard, AlertTriangle } from 'lucide-react'
import { Modal } from './Modal'
import { useUpdatePlan } from '../../hooks/useCheckout'
import { ModalObjectProps } from '../../types'
import { useAddChatToast, type ChatToast } from '../../store/toasts'
import { useSubscription } from '../../store/subscriptions'
import { useLegacyPlanSunset } from '../../hooks/use-legacy-plan-sunset'
import { usePricingEdition } from '../../hooks/use-plan-features'
import { legacyPlanSunsetCopy } from '../../utils/legacy-plan-sunset'
import { ManagePaymentMethodButton } from '../elements/ManagePaymentMethodButton'
import { Button } from '@highlight/ui'
import { isV2TrialPlan, paidPlanTierName, v2TierFor } from '@highlight/common'
import {
  asPaidPlan,
  formatCardOnFile,
  formatPlanEndDate,
  getPlanDisplayName,
  getPlanType,
} from '../../utils/billingUtils'
import { planSubscriptionFeatureList } from '@renderer/components/pricing/plan-select-cards'
import WindowIpcRenderer from '@renderer/ipc/WindowIpc.renderer'
import { isBrowserRuntime, navigateToHighlightRoute } from '@renderer/browser/browser-utils'
import { subscriptionPlansRoute } from '@shared/settings/settings-deeplink'
import { BillingRefreshNotice } from '@renderer/components/pricing/billing-refresh-notice'
import { useBillingRefreshGate } from '@renderer/components/pricing/use-billing-refresh-gate'

type ModalView = 'main' | 'cancel-confirm'

/** Cancel and reactivate copy, keyed by what the user is on. */
interface CancelCopy {
  noun: string
  title: string
  afterEnd: string
  cancelled: Omit<ChatToast, 'id'>
  reactivated: (planName: string) => Omit<ChatToast, 'id'>
}

const SUBSCRIPTION_COPY: CancelCopy = {
  noun: 'subscription',
  title: 'Subscription',
  afterEnd:
    "After that, your account will be read-only. You'll keep access to your existing meetings and notes, but won't be able to record, use chat, or run skills.",
  cancelled: {
    title: 'Subscription Cancelled',
    message: "Your subscription has been cancelled. You'll retain access until the end of your billing period.",
    type: 'success',
  },
  reactivated: () => ({
    title: 'Subscription Reactivated',
    message: "Your subscription has been reactivated. You'll have access to all premium features again.",
    type: 'success',
  }),
}

// A v2 trial with a card on file converts to a paid tier, so it cancels like a
// subscription. Without a card it ends on free by itself and gets no cancel
// button, and a legacy trial ends on free too, so neither reaches this.
const TRIAL_COPY: CancelCopy = {
  noun: 'trial',
  title: 'Trial',
  afterEnd:
    "After that, your account will be read-only and you won't be charged. You'll keep access to your existing meetings and notes, but won't be able to record, use chat, or run skills.",
  cancelled: {
    title: 'Trial Cancelled',
    message: "Your trial has been cancelled. You'll keep access until it ends and won't be charged.",
    type: 'success',
  },
  reactivated: (planName) => ({
    title: 'Trial Reactivated',
    message: `Your trial has been reactivated and will continue on ${planName} when it ends.`,
    type: 'success',
  }),
}

interface ModalShellProps {
  id: string
  title: string
  children: React.ReactNode
}

/**
 * Both views share the chat Modal primitive (portal stacking, Escape-to-close,
 * enter/exit animation) with this modal's width treatment.
 */
function ModalShell({ id, title, children }: ModalShellProps) {
  return (
    <Modal id={id} header={title} className="w-full max-w-[580px] p-6">
      {children}
    </Modal>
  )
}

export const ManageSubscriptionModal: React.FC<ModalObjectProps> = ({ id }) => {
  const { closeModal } = useModals()
  const { mutate: updatePlan, isPending: isUpdatePlanLoading } = useUpdatePlan()
  const addToast = useAddChatToast()
  const { subscription } = useSubscription()
  // A retiring Early Access grant keeps its own name and the announced end date.
  const sunset = useLegacyPlanSunset()
  const sunsetCopy = sunset ? legacyPlanSunsetCopy(sunset) : null
  const currentPlanName = sunsetCopy?.planName ?? getPlanDisplayName(subscription.plan)
  const scheduledName = paidPlanTierName(subscription.scheduledPlan?.plan)

  const [view, setView] = useState<ModalView>('main')
  const { needsBillingRefresh, commit: commitPlanChange } = useBillingRefreshGate()
  // Trials list their tier's features: what a trial is showing off is the tier
  // it converts to.
  const currentV2Tier = v2TierFor(subscription.plan)
  const pricingEdition = usePricingEdition()
  // Cancel and reactivate only ever see paid plans and v2 trials, since a legacy
  // trial gets no cancel button and is never reported cancelled.
  const copy = isV2TrialPlan(subscription.plan) ? TRIAL_COPY : SUBSCRIPTION_COPY

  // Track modal shown
  useEffect(() => {
    window.ampltd.trackEvent('Subscription', {
      action: 'manage modal shown',
    })
  }, [])

  const handleInitiateCancel = useCallback(() => {
    window.ampltd.trackEvent('Subscription', {
      action: 'cancel initiated',
      from: 'manage modal',
    })
    setView('cancel-confirm')
  }, [])

  const handleConfirmCancel = useCallback(() => {
    window.ampltd.trackEvent('Subscription', {
      action: 'cancel confirmed',
      from: 'manage modal',
    })

    updatePlan('free', {
      onSuccess: ({ billingRefreshed }) =>
        commitPlanChange(billingRefreshed, () => {
          addToast(copy.cancelled)
          closeModal(id)
        }),
      onError: () => {
        addToast({
          title: 'Cancellation Failed',
          message: `There was an error cancelling your ${copy.noun}. Please try again.`,
          type: 'error',
        })
      },
    })
  }, [updatePlan, copy, addToast, closeModal, id, commitPlanChange])

  const handleReactivateSubscription = useCallback(() => {
    window.ampltd.trackEvent('Subscription', {
      action: 'reactivate subscription',
      from: 'manage modal',
    })

    // Re-selecting the current plan unschedules the pending downgrade. A v2 trial
    // gets its conversion back and a paid plan its renewal. Legacy trials fall
    // back to pro.
    const currentPlan = isV2TrialPlan(subscription.plan) ? subscription.plan : (asPaidPlan(subscription.plan) ?? 'pro')
    updatePlan(currentPlan, {
      onSuccess: ({ billingRefreshed }) =>
        commitPlanChange(billingRefreshed, () => {
          addToast(copy.reactivated(getPlanDisplayName(currentV2Tier ?? subscription.plan)))
          closeModal(id)
        }),
      onError: () => {
        addToast({
          title: 'Reactivation Failed',
          message: `There was an error reactivating your ${copy.noun}. Please try again.`,
          type: 'error',
        })
      },
    })
  }, [updatePlan, subscription.plan, currentV2Tier, copy, addToast, closeModal, id, commitPlanChange])

  /**
   * Plans live on the Subscription settings tab now, on a full-width screen this
   * modal has no room for. The IPC reaches settings from whichever window raised
   * the modal (chat and the sidepanel both open this): a closed window loads the
   * hash route, an open one takes it through `deeplink-received`. It is also the
   * only path that doesn't bounce through the OS protocol handler, which flashes
   * the window when settings is already the one in front.
   *
   * The browser runtime has no windows to open and no-ops that IPC, so it takes
   * the page navigation instead.
   */
  const openPlansInSettings = useCallback(() => {
    window.ampltd.trackEvent('Subscription', { action: 'change plan clicked', from: 'manage modal' })
    closeModal(id)

    const route = subscriptionPlansRoute()
    if (isBrowserRuntime()) {
      navigateToHighlightRoute(route)
      return
    }
    // Nothing awaits this and the modal is already gone, so a failure would
    // otherwise be an unhandled rejection with no trace of why nothing opened.
    WindowIpcRenderer.openSettingsWindow(route).catch((error) => {
      console.error('Failed to open the plans screen in settings:', error)
    })
  }, [closeModal, id])

  const handleBackToMain = useCallback(() => {
    setView('main')
  }, [])

  // A retiring plan's date is the announced one, formatted in its zone.
  const formattedEndDate =
    sunsetCopy?.endsOn ??
    (subscription.currentPeriodEndAt ? formatPlanEndDate(subscription.currentPeriodEndAt) : 'Unknown')

  // Five outcomes share one date, and the wrong branch shows the user the wrong one.
  const planBoundaryText = (() => {
    if (sunset) return `Ends ${formattedEndDate}`
    if (subscription.isTrial) return `Trial ends ${formattedEndDate}`
    if (subscription.cancelled) return `Active until ${formattedEndDate}`
    if (subscription.scheduledPlan && scheduledName) {
      return `Changes to ${scheduledName} on ${formatPlanEndDate(subscription.scheduledPlan.startsAt)}`
    }
    return `Renews ${formattedEndDate}`
  })()

  const { isPro, isTrial } = getPlanType(subscription.plan)
  const hasProAccess = isPro || isTrial
  const showCancelButton =
    (isPro || (isV2TrialPlan(subscription.plan) && subscription.hasPaymentMethod === true)) && !subscription.cancelled

  if (needsBillingRefresh) {
    return (
      <ModalShell id={id} title="Plan change saved">
        <BillingRefreshNotice onRefreshed={() => closeModal(id)} />
      </ModalShell>
    )
  }

  if (view === 'cancel-confirm') {
    return (
      <ModalShell id={id} title={`Cancel ${copy.title}`}>
        <div className="flex flex-1 flex-col gap-4">
          <div className="flex flex-1 flex-col gap-[16px]">
            {/* Warning Message */}
            <div className="bg-warning/10 border-warning flex items-start gap-3 rounded-lg border p-3">
              <AlertTriangle className="text-warning-subtle mt-0.5 h-5 w-5 flex-shrink-0" />
              <div className="flex flex-col gap-1">
                <p className="text-warning-subtle m-0 text-sm font-semibold">You're about to cancel your {copy.noun}</p>
                <p className="text-foreground-secondary m-0 text-xs">
                  Your {copy.noun} will remain active until{' '}
                  <span className="text-foreground font-semibold">{formattedEndDate}</span>. {copy.afterEnd}
                </p>
              </div>
            </div>
          </div>

          {/* Actions - Bottom */}
          <div className="mt-auto flex w-full items-start gap-3">
            <Button variant="outline" onClick={handleBackToMain} disabled={isUpdatePlanLoading} className="w-full">
              Keep {copy.title}
            </Button>
            <Button
              variant="primary"
              tone="danger"
              onClick={handleConfirmCancel}
              disabled={isUpdatePlanLoading}
              className="w-full"
            >
              {isUpdatePlanLoading ? 'Cancelling...' : 'Confirm Cancellation'}
            </Button>
          </div>
        </div>
      </ModalShell>
    )
  }

  // Main view
  return (
    <ModalShell id={id} title="Manage Subscription">
      <div className="flex flex-1 flex-col gap-[16px]">
        <div className="flex flex-1 flex-col gap-[16px]">
          {/* Current Plan - matching PlanDetailsBlock pattern */}
          <div className="bg-foreground/[2%] flex w-full flex-col gap-3 rounded-lg border p-4 sm:flex-row sm:items-center sm:justify-between">
            {/* Left side: Plan name, status indicator, badge, and time info */}
            <div className="flex flex-wrap items-center gap-2">
              <div className="h-1.5 w-1.5 rounded-full" />
              <h3 className="text-foreground-secondary m-0 text-[15px]" style={{ fontWeight: 400 }}>
                {currentPlanName}
              </h3>
              {hasProAccess && (
                <div className="bg-element flex items-center justify-center gap-[10px] rounded-lg px-2 py-[3px]">
                  <span className="text-foreground-secondary text-[13px] font-normal leading-[20px]">
                    {sunset ? 'Retiring' : subscription.isTrial ? 'Trial' : 'Current'}
                  </span>
                </div>
              )}
              {hasProAccess && formattedEndDate !== 'Unknown' && (
                <div className="flex items-start justify-start gap-[10px] pl-2">
                  <span className="text-foreground-placeholder text-sm">{planBoundaryText}</span>
                </div>
              )}
            </div>
          </div>

          {/* Plan Details - Payment Method and Cancellation Status */}
          {hasProAccess && (
            <div className="border-border bg-foreground/[2%] flex w-full flex-col gap-3 rounded-lg border p-4">
              <div className="flex items-start gap-3">
                <CreditCard className="text-foreground-secondary mt-0.5 h-5 w-5 flex-shrink-0" />
                <div className="flex flex-col gap-1">
                  <p className="text-foreground-secondary m-0 text-sm font-medium" style={{ fontWeight: 400 }}>
                    Payment Method
                  </p>
                  <p className="text-foreground-secondary m-0 text-sm">
                    {subscription.cardOnFile
                      ? formatCardOnFile(subscription.cardOnFile)
                      : subscription.hasPaymentMethod
                        ? 'Card on file'
                        : subscription.isTrial && !sunset
                          ? 'Trial (No card required)'
                          : 'No payment method'}
                  </p>
                </div>
              </div>

              {sunsetCopy && (
                <div className="bg-warning/10 border-warning flex items-start gap-3 rounded-lg border p-3">
                  <AlertTriangle className="text-warning-subtle mt-0.5 h-5 w-5 flex-shrink-0" />
                  <div className="flex flex-col gap-1">
                    <p className="text-warning-subtle m-0 text-sm font-semibold">Plan Retiring</p>
                    <p className="text-foreground-secondary m-0 text-xs">{`${sunsetCopy.headline}. ${sunsetCopy.nextStep}`}</p>
                  </div>
                </div>
              )}

              {subscription.cancelled && (
                <div className="bg-warning/10 border-warning flex items-start gap-3 rounded-lg border p-3">
                  <AlertTriangle className="text-warning-subtle mt-0.5 h-5 w-5 flex-shrink-0" />
                  <div className="flex flex-col gap-1">
                    <p className="text-warning-subtle m-0 text-sm font-semibold">
                      {subscription.isTrial ? 'Trial Cancelled' : 'Subscription Cancellation Scheduled'}
                    </p>
                    <p className="text-foreground-secondary m-0 text-xs">
                      {subscription.isTrial
                        ? `Your trial will end on ${formattedEndDate}. You can reactivate anytime before then.`
                        : `Your ${getPlanDisplayName(subscription.plan)} access will end on ${formattedEndDate}. You can reactivate anytime before then.`}
                    </p>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Pro Features */}
          {hasProAccess && (
            <div className="border-border bg-foreground/[2%] flex w-full flex-col gap-3 rounded-lg border p-4">
              <p className="text-foreground-secondary m-0 text-sm font-medium" style={{ fontWeight: 400 }}>
                Your {currentPlanName} features:
              </p>
              {currentV2Tier ? (
                <PlanAttributes attributes={planSubscriptionFeatureList(currentV2Tier, pricingEdition)} />
              ) : (
                <PlanAttributes attributes={LEGACY_PRO_ATTRIBUTES} />
              )}
            </div>
          )}
        </div>

        {/* Actions - Bottom */}
        <div className="mt-auto flex w-full flex-col gap-3">
          {hasProAccess && subscription.hasPaymentMethod && (
            <ManagePaymentMethodButton from="manage subscription modal" variant="outline" size="md" />
          )}

          {hasProAccess && (
            <Button variant="outline" onClick={openPlansInSettings} className="w-full">
              Change Plan
            </Button>
          )}

          {showCancelButton && (
            <div className="flex items-center justify-start">
              <Button variant="primary" tone="danger" onClick={handleInitiateCancel} className="w-full">
                Cancel {copy.title}
              </Button>
            </div>
          )}

          {hasProAccess && subscription.cancelled && (
            <Button
              variant="primary"
              onClick={handleReactivateSubscription}
              disabled={isUpdatePlanLoading}
              className="w-full"
            >
              Don't Cancel My {copy.title}
            </Button>
          )}
        </div>
      </div>
    </ModalShell>
  )
}
